/**
 * Release-notes voor de versie-modal in de admin-footer.
 * Bron-documenten: docs/UPDATES-*.md — houd die in sync bij een bump.
 *
 * Nieuwste eerst in APP_VERSION_NOTES_REGISTRY; de modal toont een
 * versielijst zodat je ook eerdere releases kunt openen.
 */
export interface VersionNotesSection {
  title: string;
  items: string[];
}

export interface VersionNotes {
  version: string;
  dateLabel: string;
  intro: string;
  sections: VersionNotesSection[];
  docPath: string;
}

/** Actuele productversie (zelfde string als package.json, zonder “v”). */
export const NOTES_1_0_5: VersionNotes = {
  version: '1.0.5',
  dateLabel: '4 – 10 oktober 2026',
  intro:
    'StatsPerform (Opta) volledig in de keten, R&D/Brook automatisch, Integrations- en League-pagina in beheer, spelers en clubs aan de catalogus, en Live/Stats in de webapp.',
  docPath: 'docs/UPDATES-2026-10-10.md',
  sections: [
    {
      title: 'StatsPerform (Opta) & league-omgevingen',
      items: [
        'Events op de wedstrijd, clips in de studio, sandbox; koppeltabel per ploeg (#658, #660, #662)',
        'Inplannen: op veld + camerapositie, half uur voor aftrap, woensdagnacht + nachtelijke controle, beker/play-offs (#677, #686, #698, #699, #701)',
        'Clubnamen thuis/uit vergelijken, Jong-teams, logo’s uit de catalogus; Test connection per competitie (#683, #687, #688, #697, #726, #727)',
        'League-omgevingen: alle wedstrijden en events, deelnemers uit het speelschema; Opta-correcties; REST-terugval (#684, #700, #705, #728, #729)',
        'Fusion: club met alleen club_admin, opname alsnog plannen als er geen camera paste (#709, #710)',
      ],
    },
    {
      title: 'R&D (Brook) & Integrations',
      items: [
        'Goedgekeurde analyses automatisch ophalen; handmatig controleren/ophalen; foutoorzaak zichtbaar (#746, #748, #749, #757, #759)',
        'R&D-import: analyse klaar, geen dubbele events; rem op Brook en tab R&D matches (#707, #760, #761)',
        'Nieuwe pagina Integrations: kaart per partner, echte Brook-statussen in vijf fases, totalen (#763–#766)',
        'League-pagina opgeruimd: keten, Needs attention, volgende stap per wedstrijd (#772)',
        'Beheer R&D volledig Engelstalig (#750)',
      ],
    },
    {
      title: 'Clubs, spelers & leagues',
      items: [
        'Pagina Players met alle spelers; league-album aan de echte club koppelen (#689, #691, #692, #694)',
        'Clubs aan de catalogus koppelen, Catalogue check, logo-upload hersteld, zoekcache (#628, #718, #721, #732)',
        'Pakket, seizoen en amateur per club; module AI-camera; studio-monitor altijd aan (#665, #703, #735)',
        'Sportlink: thuiswedstrijden automatisch, club kiezen met zoekveld (#712, #739)',
        'Club ziet league-wedstrijden; League apart in de webapp; clubs achteraf aanpassen (#667, #671, #768)',
        'Onboarding: welkomstvenster, Getting started en rondleiding (#706); filters blijven na verversen (#630)',
      ],
    },
    {
      title: 'SciSports & KNVB',
      items: [
        'SciSports: bestandsnaam per wedstrijd, MatchDelivery + uploadlink en -status (#685, #690)',
        'KNVB: zelfde namen als SciSports, uploadlink per bestand, SFTP vanaf de server (#693, #695, #702)',
        'Wedstrijdformulier: korte ploegnamen, doorlopende namen in wissels (#678, #681)',
      ],
    },
    {
      title: 'Live, studio & camera’s',
      items: [
        'Beheer: live meekijken per camera; Improvispo Seat ID onder Devices; producer-velden leesbaar (#708, #751–#754)',
        'Tag-sessie per camera met wedstrijd uit de planning; cameraposities blijven; clips uit = alleen opnemen (#713, #714, #716, #740, #769)',
        'Event-clips niet meer als wedstrijdcamera, met naam van event en camera (#755, #770, #771)',
        'Live (webapp): scorebord met logo’s, Opta-klok, eventfilter, clip terugkijken, clip met tekening naar de bank (#704, #720, #722, #737, #741, #743–#745)',
        'Live-speler stabiel: geen opgeven na een minuut, geen zwart beeld na herverbinding; iPhone 16:9 (#733, #736, #738)',
      ],
    },
    {
      title: 'Stats, zoeken & video (webapp)',
      items: [
        'Stats-pagina per wedstrijd: visuals, passpijlen, duels, vakken over het veld, rapport op A4 (#636–#638, #643, #645, #647, #650, #656, #657)',
        'Zoeken naar momenten over wedstrijden, inclusief SciSports-sequenties (#639, #640, #642)',
        'Spelerprofiel, minikaart, overlay van het lopende event, commentaar als ondertitel (#644, #649, #651, #674)',
        'Wedstrijdenlijst: ontvangen analyses en status bij de taggingpartner (#747, #767)',
      ],
    },
    {
      title: 'iOS, beveiliging & overig',
      items: [
        'iOS-app voor TestFlight en handleiding iPad; tv-proef weer weg (#635, #655, #676)',
        'Clubcontrole op opstelling, upload, teams en clubalbums (#663); geen camerawachtwoorden in de log (#711)',
        'Onbekende wedstrijd geeft 404; bolhoeken na upload; platform-default bij nieuwe wedstrijd (#646, #715, #756)',
      ],
    },
  ],
};

export const NOTES_1_0_4: VersionNotes = {
  version: '1.0.4',
  dateLabel: 'eind september – 3 oktober 2026',
  intro:
    'Grote bundel: club-catalogus (CSV/API + logos), Europe-sync, tegenstander-zoek, teamcatalogus, imports (KNVB/COMET) en beheerconsole.',
  docPath: 'docs/UPDATES-2026-10-03.md',
  sections: [
    {
      title: 'Club catalog & API-Football',
      items: [
        'Sidebar Club catalog: MySQL-rijen met logo-URL (API + CSV) (#598)',
        'Sync European pro clubs → MySQL; errors/rate-limit; geen page-param (#584, #598–#599)',
        'CSV → MySQL + land-scope NL↔BE; Test search API+CSV; Gilze/Feyenoord-fixes (#546, #574–#576)',
      ],
    },
    {
      title: 'Tegenstander (webapp)',
      items: [
        'Choose opponent: logo’s; geen dubbele namen (logo wint) (#601, #604)',
        'Assign recording via catalogus/API; aanvraag i.p.v. vrije naam (#551, #611)',
      ],
    },
    {
      title: 'Teams, spelers, clubs',
      items: [
        'Teamcatalogus: jongens/meisjes, team 3, U9/U10, Seniors, External (#596)',
        'Spelers: Global ID alleen super_admin; lijst/formulier (#558, #609)',
        'Admin Clubs: soft-delete, status/opslag-filter, gebruikers per club; Club albums weg (#557, #560, #565, #578)',
      ],
    },
    {
      title: 'Data-imports',
      items: [
        'KNVB-wedstrijdformulier; Match reports COMET + AI-PDF; SciSports/LongoMatch (#577, #591, #597, #623)',
        'Proefimport per bron; upload via link (opslag, playlist, SharePoint) (#579, #586–#587)',
      ],
    },
    {
      title: 'Live, video, studio (webapp)',
      items: [
        'Live/TV: AirPlay/Chromecast, fullscreen telefoon, panorama iPad (#603, #605–#608, #616, #620–#621)',
        'Videospeler telefoon: camera’s, knoppenbalk, fullscreen-navigatie (#617–#619, #622)',
        'Studio/tagging opgeruimd; scorebord; stats-voorbereiding (#589, #610–#614)',
      ],
    },
  ],
};

export const NOTES_1_0_3F: VersionNotes = {
  version: '1.0.3f',
  dateLabel: '2 oktober 2026',
  intro:
    'Club catalog CRUD met logo-URL; API-Europe sync toont echte fouten i.p.v. “0 teams / Not synced yet”.',
  docPath: 'docs/CLUB_TABLE_REORG.md',
  sections: [
    {
      title: 'Club catalog',
      items: [
        'Sidebar → Club catalog: MySQL-rijen met logo-veld (API + CSV)',
        'Sync Europe: errors/rate-limit uit API-body, landen via /teams/countries',
        'Status: “Not synced yet” alleen als er echt nog geen sync-poging was',
      ],
    },
  ],
};

export const NOTES_1_0_3E: VersionNotes = {
  version: '1.0.3e',
  dateLabel: '30 september 2026',
  intro:
    'On-the-fly tegenstander via HollandseVelden-catalogus; CSV-upload in admin.',
  docPath: 'docs/CLUB_TABLE_REORG.md',
  sections: [
    {
      title: 'Club-catalogus',
      items: [
        'Clubs → Club catalog: CSV → MySQL (overleeft auth-rebuild)',
        'Land-scope: NL/BE-clubs zien NL+BE in de vergaarbak',
        'Webapp Uitclub: alleen Bekend/Nieuw, geen vrije naam of HV/API-jargon',
      ],
    },
  ],
};

export const NOTES_1_0_3D: VersionNotes = {
  version: '1.0.3d',
  dateLabel: '26–27 september 2026',
  intro:
    'Vervolg op v1.0.3c. Match-detail hub, wedstrijden/video’s-lijsten, leagues apart, overlay-opstelling.',
  docPath: 'docs/UPDATES-2026-09-27d.md',
  sections: [
    {
      title: 'Match-detail hub',
      items: [
        'Zes frames (Info / Video / Tagging / Live / Line-up / Control) (#434–#437)',
        'Statuspreviews, framing, SID, Control-imports, Live + toolbars (#438–#447)',
        'Speelers overlay op veld; formatie in subheader; IDs in header-midden (#448–#458)',
      ],
    },
    {
      title: 'Wedstrijden-lijst',
      items: [
        'Tabel/kaart, scorebord, kopregel, vaste zoekbalk (#412–#415)',
        'Footer-statusbalk; page-size 20/50/100; fase-tabs CAPS (#416–#419)',
      ],
    },
    {
      title: 'Video’s',
      items: [
        'Layout als Matches; intro-beeld i.p.v. match-chrome (#424–#425)',
        'Filter met/zonder video, toggle, hover-preview (#427–#433)',
        'Lege events-tijdlijnbalk onder speler weg (#428)',
      ],
    },
    {
      title: 'Leagues / admin / overig',
      items: [
        'Leagues apart van Clubs (#426); Clubs *ngIf-hotfix (#429)',
        'siq_indite set_auto_opname (#423); Dataduct ASCII Content-Disposition (#454)',
      ],
    },
  ],
};

export const NOTES_1_0_3C: VersionNotes = {
  version: '1.0.3c',
  dateLabel: '25 september 2026',
  intro:
    'Vervolg op v1.0.3b. Opstelling-slots, Brook lineups/squad, stabiele competition + season, superadmin-fixes.',
  docPath: 'docs/UPDATES-2026-09-25c.md',
  sections: [
    {
      title: 'Opstelling (webapp)',
      items: [
        'Sleep zonder positielabel (#400)',
        'Wedstrijden-tabel: vierkante outline-iconen (#401)',
        'Lokale slots 1/2/3, A/R-vulling, reservebank (#402)',
      ],
    },
    {
      title: 'Brook intake — lineups & squad',
      items: [
        'Push stuurt squad + lineups (#403–#405)',
        'Testing: lineup-tabel + POST-JSON mét lineups[] (#406, #409)',
        'Squad = heel clubalbum; Brook GET toont alleen telling',
      ],
    },
    {
      title: 'Brook intake — competition & season',
      items: [
        'Stabiele comp:…-id i.p.v. volle naam (#410)',
        'Apart seasonExternalId (2026/2027), niet in de naam',
      ],
    },
    {
      title: 'Fixes (superadmin)',
      items: [
        'GET /match/{sid} zonder lege club-filter (#407)',
        '/players?clubAlbum= niet leeg door teams-claim (#408)',
      ],
    },
  ],
};

export const NOTES_1_0_3B: VersionNotes = {
  version: '1.0.3b',
  dateLabel: '25 september 2026',
  intro:
    'Vervolg op v1.0.3a. Bundel van wat sindsdien op dev is geland (wedstrijdentabel, matchsheets, federatie/uitclub).',
  docPath: 'docs/UPDATES-2026-09-25.md',
  sections: [
    {
      title: 'Wedstrijden-tabel vs Video’s',
      items: [
        'Menu: Wedstrijden vóór Video’s (#393)',
        'Tabel + fase pending/actual/finished; Video’s = beeld (#394, #395)',
        'Aanlever-chips Video/Rapport/Opstelling; grijs / donkergrijs / rood (#396–#398)',
      ],
    },
    {
      title: 'Matchsheets / speelschema',
      items: [
        'COMET startlijst/rapport → opstelling, spelers, uitslag (#376)',
        'COMET zonder AI-sleutel; speelrondes zichtbaar (#378, #379)',
        'Ontbrekende wedstrijden aanmaken; alle albums in Spelers (#390–#392)',
      ],
    },
    {
      title: 'Uitclub / federatie / push',
      items: [
        'Federatie-account voor league-omgevingen (#372)',
        'Uitclub typen/modal/federatiepeers (#384–#386)',
        'Push clubIds + scheduledAt (#387, #388)',
      ],
    },
    {
      title: 'Video / login / Open-als-club',
      items: [
        'Video via link (Veo/Reeplayer) (#373, #375)',
        'Global alleen super_admin; footer PR#N (#370, #371)',
        'Open-als-club scope + juiste club; login 401; super_admin niet in webapp (#377, #381–#383)',
      ],
    },
  ],
};

export const NOTES_1_0_3A: VersionNotes = {
  version: '1.0.3a',
  dateLabel: '23–24 september 2026',
  intro:
    'Vervolg op UPDATES-2026-09-22. Bundel van wat sindsdien op dev is geland.',
  docPath: 'docs/UPDATES-2026-09-24.md',
  sections: [
    {
      title: 'Spelers: twee lagen (GlobalPlayer)',
      items: [
        'Besluit club-speler + optionele global (#353)',
        'Fase 1 API: CreateGlobal / link pending→linked (#359)',
        'Fase 2 club-UI koppelen; linked = persoonsvelden read-only (#361)',
        'Modal simpeler + DRY toevoegen/bewerken (#363, #364)',
        'Global-ID alleen framework-admin; 48 hex; duidelijkere labels (#365)',
        'Admin-lijst GlobalPlayers (#366)',
      ],
    },
    {
      title: 'Versiemarker & footer',
      items: [
        'Altijd branch@sha via write-frontend-build-info (#362)',
        'Klik branch@sha → laatste 10 deploy-merges (#367, #368)',
        'Klik v1.0.3a → release-notes',
      ],
    },
    {
      title: 'League / Qatar FA',
      items: [
        'League-omgeving fase 1 + contributor (#352, #355)',
        'Speelschema importeren uit COMET-PDF (#360)',
      ],
    },
    {
      title: 'Tagging / panelen / live',
      items: [
        'Paneel import/export, editor, globale panelen bij clubs',
        'Wedstrijdklok / halves / Start–End match-knop',
        'Coach mag panelen niet bewerken; scoping op eigen club',
      ],
    },
    {
      title: 'Beveiliging & overig',
      items: [
        'Profiel kan rol/teams niet meer wijzigen (#354)',
        'Clubnaam in webapp-kop; PR’s in het Nederlands',
      ],
    },
  ],
};

/**
 * Nieuwste eerst. Huidige productversie = index 0 (of match op package.json).
 */
export const APP_VERSION_NOTES_REGISTRY: VersionNotes[] = [
  NOTES_1_0_5,
  NOTES_1_0_4,
  NOTES_1_0_3F,
  NOTES_1_0_3E,
  NOTES_1_0_3D,
  NOTES_1_0_3C,
  NOTES_1_0_3B,
  NOTES_1_0_3A,
];

/** Convenience: actuele notes (eerste in de registry). */
export const APP_VERSION_NOTES: VersionNotes = APP_VERSION_NOTES_REGISTRY[0];

export function notesForVersion(
  version: string | null | undefined,
): VersionNotes {
  const v = (version ?? '').replace(/^v/i, '');
  return (
    APP_VERSION_NOTES_REGISTRY.find((n) => n.version === v) ??
    APP_VERSION_NOTES
  );
}
